'use client';

import { forwardRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

export type ReCaptchaProps = {
  onCaptchaChange: (token: string) => void;
  lng?: string;
  theme?: 'dark' | 'light';
  hideRecaptcha?: boolean;
};

export type ReCaptchaRef = {
  execute: () => void;
  reset: () => void;
};

/**
 * Google ReCAPTCHA component wrapper
 * @component
 * @param {ReCaptchaProps} props - Component props
 * @param {React.Ref<ReCaptchaRef>} ref - Forwarded ref for execute/reset methods
 * @returns {JSX.Element | null} ReCAPTCHA component
 */
const ReCaptchaWidget = forwardRef<ReCaptchaRef, ReCaptchaProps>(
  ({ onCaptchaChange, lng, theme = 'dark', hideRecaptcha = false }, ref): JSX.Element | null => {
    const siteKey = '6Le1LMgZAAAAAB2FSmwjtjiosGJmSqc1g6t95rrY';

    useEffect(() => {
      if (hideRecaptcha) {
        const style = document.createElement('style');
        style.textContent = '.grecaptcha-badge { display: none; }';
        document.head.appendChild(style);

        return () => {
          document.head.removeChild(style);
        };
      }
      return undefined;
    }, [hideRecaptcha]);

    if (typeof window === 'undefined') return null;

    return (
      <ReCAPTCHA
        id="recaptcha"
        sitekey={siteKey}
        theme={theme}
        ref={ref}
        hl={lng}
        size="invisible"
        onChange={onCaptchaChange}
        badge="inline"
      />
    );
  }
);

ReCaptchaWidget.displayName = 'ReCaptchaWidget';

export default ReCaptchaWidget;
